/**
 * Bulk Price Creation Modal — 3-step wizard.
 */

import { DateRangePicker, DateRangePickerLocale } from '../lib/date-range-picker';
import { postJson, fetchJson } from '../lib/api-client';
import { showNotification, escapeHtml } from '../lib/helpers';

export function init(): void {
    const modal = document.getElementById('bulkPriceModal');
    if (!modal) return;

    // URL resolution — try Index container first, then Details container
    const indexContainer = document.getElementById('offer-index-container');
    const detailsContainer = document.getElementById('offer-details-container');
    const container = indexContainer ?? detailsContainer;
    if (!container) return;

    const urlGetUnits  = container.dataset.urlGetUnitsForPricing ?? '';
    const urlSaveBulk  = container.dataset.urlBulkPrices ?? '';

    function buildLocale(el: HTMLElement): DateRangePickerLocale {
        const months = el.dataset.locMonths?.split(',');
        const days   = el.dataset.locDays?.split(',');
        return {
            months:      months?.length === 12 ? months : undefined,
            days:        days?.length   === 7  ? days   : undefined,
            apply:       el.dataset.locApply,
            clear:       el.dataset.locClear,
            placeholder: el.dataset.locSelectDates,
            selectStart: el.dataset.locSelectStart,
        };
    }

    const locActive    = () => modal.dataset.locActive    ?? 'Active';
    const locInactive  = () => modal.dataset.locInactive  ?? 'Inactive';
    const locContinue  = () => modal.dataset.locContinue  ?? 'Continue';
    const locSelected  = () => modal.dataset.locSelected  ?? 'selected';
    const locAppliesTo = () => modal.dataset.locAppliesTo ?? 'Applies to';

    // ── UI refs ──────────────────────────────────────
    const step1El          = document.getElementById('bulk-price-step-1')!;
    const step2El          = document.getElementById('bulk-price-step-2')!;
    const step3El          = document.getElementById('bulk-price-step-3')!;
    const loadingEl        = document.getElementById('bulk-units-loading')!;
    const unitListWrapper  = document.getElementById('bulk-unit-list-wrapper')!;
    const unitRowsEl       = document.getElementById('bulk-unit-rows')!;
    const selectAllChk     = document.getElementById('bulk-select-all') as HTMLInputElement;
    const countLabel       = document.getElementById('bulk-unit-count-label')!;
    const sharedPanel      = document.getElementById('bulk-shared-panel')!;
    const perUnitPanel     = document.getElementById('bulk-per-unit-panel')!;
    const sharedTbody      = document.getElementById('bulk-shared-tbody')!;
    const sharedAddBtn     = document.getElementById('bulk-shared-add-row')!;
    const perUnitBlocks    = document.getElementById('bulk-per-unit-blocks')!;
    const reviewTbody      = document.getElementById('bulk-review-tbody')!;
    const reviewSummary    = document.getElementById('bulk-review-summary')!;
    const offerNameEl      = document.getElementById('bulk-price-offer-name')!;
    const sharedUnitsLabel = document.getElementById('bulk-shared-units-label')!;
    const footerHint       = document.getElementById('bulk-footer-hint')!;
    const continueBtn      = document.getElementById('bulk-continue-btn') as HTMLButtonElement;
    const reviewBtn        = document.getElementById('bulk-review-btn') as HTMLButtonElement;
    const saveBtn          = document.getElementById('bulk-save-btn') as HTMLButtonElement;
    const backBtn          = document.getElementById('bulk-back-btn') as HTMLButtonElement;
    const modeSharedBtn    = document.getElementById('bulk-mode-shared') as HTMLButtonElement;
    const modePerUnitBtn   = document.getElementById('bulk-mode-per-unit') as HTMLButtonElement;
    const partnerHeader    = document.getElementById('bulk-partner-header');
    const seatsHeader      = document.getElementById('bulk-seats-header');

    const stepIndicators   = [document.getElementById('bpi-step-1'), document.getElementById('bpi-step-2'), document.getElementById('bpi-step-3')];

    // ── State ─────────────────────────────────────────
    type UnitEntry = { id: number; name: string; typeName: string; classification: string; service: string; priceCount: number };
    type PriceRow  = { id: number; price: string; partnerPrice: string; from: string | null; to: string | null; status: number; seats: string };

    let currentStep = 1;
    let currentOfferId = 0, currentOfferGroup = '', agencyAcceptsPartnership = true;
    let units: UnitEntry[] = [];
    let selectedIds: Set<number> = new Set();
    let mode: 'shared' | 'per-unit' = 'shared';
    let sharedRows: PriceRow[] = [];
    let perUnitRows: Map<number, PriceRow[]> = new Map();
    let pickers: DateRangePicker[] = [];
    let nextRowId = 1;
    let skippedStep1 = false;

    function blankRow(): PriceRow {
        return { id: nextRowId++, price: '', partnerPrice: '', from: null, to: null, status: 1, seats: '1' };
    }

    function isTransport(): boolean { return currentOfferGroup === 'Transport'; }

    // ── Open trigger ──────────────────────────────────
    document.addEventListener('click', async (e) => {
        const btn = (e.target as HTMLElement).closest('.btn-open-bulk-prices') as HTMLElement | null;
        if (!btn) return;

        currentOfferId = parseInt(btn.dataset.offerId ?? '0', 10);
        currentOfferGroup = btn.dataset.offerGroup ?? '';
        agencyAcceptsPartnership = btn.dataset.agencyAcceptsPartnership !== 'false';
        const offerName = btn.dataset.offerName ?? '';
        const preUnitId = parseInt(btn.dataset.preUnitId ?? '0', 10);

        offerNameEl.textContent = offerName ? `— ${offerName}` : '';
        resetModal();

        new window.bootstrap.Modal(modal).show();

        // Load units
        try {
            const res = await fetchJson<{ success: boolean; data: UnitEntry[] }>(`${urlGetUnits}?offerId=${currentOfferId}`);
            units = res.success ? res.data : [];
        } catch {
            units = [];
        }

        loadingEl.classList.add('d-none');
        unitListWrapper.classList.remove('d-none');

        if (preUnitId > 0) {
            // Pre-select the single unit and jump straight to step 2
            skippedStep1 = true;
            selectedIds = new Set([preUnitId]);
            selectedIds.forEach(uid => {
                if (!perUnitRows.has(uid)) perUnitRows.set(uid, [blankRow()]);
            });
            renderStep2();
            goToStep(2);
            backBtn.classList.add('d-none');
        } else {
            renderUnitList();
        }
    });

    // ── Modal cleanup ─────────────────────────────────
    modal.addEventListener('hidden.bs.modal', () => {
        destroyPickers();
        unitRowsEl.innerHTML = '';
        sharedTbody.innerHTML = '';
        perUnitBlocks.innerHTML = '';
        reviewTbody.innerHTML = '';
    });

    // ── Reset ─────────────────────────────────────────
    function resetModal(): void {
        currentStep = 1;
        units = [];
        selectedIds = new Set();
        mode = 'shared';
        sharedRows = [blankRow()];
        perUnitRows = new Map();
        skippedStep1 = false;
        destroyPickers();

        goToStep(1);
        unitListWrapper.classList.add('d-none');
        loadingEl.classList.remove('d-none');
        unitRowsEl.innerHTML = '';
        continueBtn.disabled = true;
        continueBtn.textContent = `${locContinue()} →`;
    }

    function destroyPickers(): void {
        pickers.forEach(p => p.destroy());
        pickers = [];
    }

    // ── Step navigation ───────────────────────────────
    function goToStep(step: number): void {
        currentStep = step;
        step1El.classList.toggle('d-none', step !== 1);
        step2El.classList.toggle('d-none', step !== 2);
        step3El.classList.toggle('d-none', step !== 3);
        continueBtn.classList.toggle('d-none', step !== 1);
        reviewBtn.classList.toggle('d-none', step !== 2);
        saveBtn.classList.toggle('d-none', step !== 3);
        backBtn.classList.toggle('d-none', step === 1);

        stepIndicators.forEach((el, i) => {
            if (!el) return;
            el.classList.toggle('active', i + 1 === step);
            el.classList.toggle('done', i + 1 < step);
        });
    }

    continueBtn.addEventListener('click', () => {
        if (selectedIds.size === 0) return;
        // Initialize per-unit rows
        selectedIds.forEach(uid => {
            if (!perUnitRows.has(uid)) perUnitRows.set(uid, [blankRow()]);
        });
        renderStep2();
        goToStep(2);
    });

    reviewBtn.addEventListener('click', () => {
        renderReview();
        goToStep(3);
    });

    backBtn.addEventListener('click', () => {
        if (currentStep === 2) {
            if (skippedStep1) {
                window.bootstrap.Modal.getInstance(modal)?.hide();
            } else {
                destroyPickers();
                goToStep(1);
            }
        } else if (currentStep === 3) {
            goToStep(2);
            if (skippedStep1) backBtn.classList.add('d-none');
        }
    });

    // ── Step 1: unit list ─────────────────────────────
    function renderUnitList(): void {
        unitRowsEl.innerHTML = '';
        units.forEach(u => {
            const row = document.createElement('div');
            row.className = 'bulk-unit-row' + (selectedIds.has(u.id) ? ' selected' : '');
            row.dataset.unitId = String(u.id);
            row.innerHTML = `
                <input type="checkbox" class="form-check-input bulk-unit-chk" ${selectedIds.has(u.id) ? 'checked' : ''} />
                <div class="bulk-unit-info flex-grow-1 ms-2">
                    <span class="fw-bold">${escapeHtml(u.name)}</span>
                    <span class="text-muted small ms-2">${escapeHtml([u.typeName, u.classification, u.service].filter(Boolean).join(' · '))}</span>
                </div>
                <span class="bulk-unit-price-count text-muted small">${u.priceCount > 0 ? `${u.priceCount} price${u.priceCount !== 1 ? 's' : ''}` : ''}</span>
                ${selectedIds.has(u.id) ? '<svg class="bulk-unit-check" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="16" height="16" stroke-width="2" stroke="#05a677"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg>' : ''}
            `;
            row.addEventListener('click', (e) => {
                const chk = row.querySelector('.bulk-unit-chk') as HTMLInputElement;
                if ((e.target as HTMLElement) !== chk) chk.checked = !chk.checked;
                if (chk.checked) selectedIds.add(u.id);
                else selectedIds.delete(u.id);
                renderUnitList();
                updateContinueBtn();
            });
            unitRowsEl.appendChild(row);
        });
        updateSelectAll();
    }

    function updateContinueBtn(): void {
        continueBtn.disabled = selectedIds.size === 0;
        const base = locContinue();
        continueBtn.textContent = selectedIds.size > 0 ? `${base} (${selectedIds.size}) →` : `${base} →`;
    }

    function updateSelectAll(): void {
        selectAllChk.checked = units.length > 0 && selectedIds.size === units.length;
        countLabel.textContent = `${selectedIds.size} / ${units.length} ${locSelected()}`;
    }

    selectAllChk.addEventListener('change', () => {
        if (selectAllChk.checked) units.forEach(u => selectedIds.add(u.id));
        else selectedIds.clear();
        renderUnitList();
        updateContinueBtn();
    });

    // ── Step 2: price grids ───────────────────────────
    function renderStep2(): void {
        destroyPickers();
        const names = [...selectedIds].map(id => units.find(u => u.id === id)?.name ?? '').join(', ');
        sharedUnitsLabel.textContent = `${locAppliesTo()}: ${names}`;

        if (partnerHeader) partnerHeader.classList.toggle('d-none', !agencyAcceptsPartnership);
        if (seatsHeader) seatsHeader.classList.toggle('d-none', !isTransport());

        renderSharedGrid();
        if (mode === 'per-unit') {
            renderPerUnitBlocks();
            sharedPanel.classList.add('d-none');
            perUnitPanel.classList.remove('d-none');
        } else {
            sharedPanel.classList.remove('d-none');
            perUnitPanel.classList.add('d-none');
        }
    }

    modeSharedBtn.addEventListener('click', () => {
        mode = 'shared';
        modeSharedBtn.classList.add('active');
        modePerUnitBtn.classList.remove('active');
        destroyPickers();
        sharedPanel.classList.remove('d-none');
        perUnitPanel.classList.add('d-none');
        renderSharedGrid();
    });

    modePerUnitBtn.addEventListener('click', () => {
        mode = 'per-unit';
        modePerUnitBtn.classList.add('active');
        modeSharedBtn.classList.remove('active');
        destroyPickers();
        sharedPanel.classList.add('d-none');
        perUnitPanel.classList.remove('d-none');
        renderPerUnitBlocks();
    });

    // ── Price grid shared ─────────────────────────────
    function renderSharedGrid(): void {
        destroyPickers();
        sharedTbody.innerHTML = '';
        sharedRows.forEach((row, idx) => appendPriceRow(sharedTbody, row, idx, 'shared', null));
    }

    sharedAddBtn.addEventListener('click', () => {
        sharedRows.push(blankRow());
        renderSharedGrid();
    });

    // ── Price grid per-unit ───────────────────────────
    function renderPerUnitBlocks(): void {
        destroyPickers();
        perUnitBlocks.innerHTML = '';
        selectedIds.forEach(uid => {
            const unit = units.find(u => u.id === uid);
            if (!unit) return;

            const rows = perUnitRows.get(uid) ?? [];
            perUnitRows.set(uid, rows);

            const block = document.createElement('div');
            block.className = 'per-unit-block mb-4';
            block.dataset.unitId = String(uid);
            block.innerHTML = `
                <div class="per-unit-block-header d-flex justify-content-between align-items-center mb-2 px-3 py-2 rounded">
                    <span class="fw-bold">${escapeHtml(unit.name)}</span>
                    <span class="text-muted small">${escapeHtml([unit.typeName, unit.service].filter(Boolean).join(' · '))}</span>
                </div>
                <table class="units-grid w-100">
                    <thead><tr>
                        <th class="col-num">#</th>
                        <th>Price *</th>
                        ${agencyAcceptsPartnership ? '<th>Partner price</th>' : ''}
                        <th>Date range *</th>
                        <th>Status</th>
                        ${isTransport() ? '<th>Seats</th>' : ''}
                        <th class="col-actions"></th>
                    </tr></thead>
                    <tbody class="per-unit-tbody" data-unit-id="${uid}"></tbody>
                </table>
                <button type="button" class="btn-add-row w-100 per-unit-add-row" data-unit-id="${uid}">+ Add period</button>
            `;
            perUnitBlocks.appendChild(block);

            const tbody = block.querySelector('.per-unit-tbody') as HTMLElement;
            rows.forEach((row, idx) => appendPriceRow(tbody, row, idx, 'per-unit', uid));
        });

        perUnitBlocks.querySelectorAll('.per-unit-add-row').forEach(btn => {
            btn.addEventListener('click', () => {
                const uid = parseInt((btn as HTMLElement).dataset.unitId ?? '0', 10);
                const rows = perUnitRows.get(uid) ?? [];
                rows.push(blankRow());
                perUnitRows.set(uid, rows);
                renderPerUnitBlocks();
            });
        });
    }

    // ── Append a single price row ─────────────────────
    function appendPriceRow(tbody: HTMLElement, row: PriceRow, idx: number, scope: 'shared' | 'per-unit', uid: number | null): void {
        const tr = document.createElement('tr');
        tr.dataset.rowId = String(row.id);

        const drpBtn = document.createElement('button');
        drpBtn.type = 'button';
        drpBtn.className = 'drp-trigger btn btn-sm';
        drpBtn.title = 'Select date range';

        const picker = new DateRangePicker(drpBtn, {
            initialFrom: row.from,
            initialTo: row.to,
            onChange: (from, to) => {
                row.from = from;
                row.to = to;
            },
            locale: buildLocale(modal)
        });
        pickers.push(picker);

        // Status select
        const statusSel = document.createElement('select');
        statusSel.className = 'form-select form-select-sm';
        statusSel.innerHTML = `<option value="0">${locInactive()}</option><option value="1" selected>${locActive()}</option>`;
        statusSel.value = String(row.status);
        statusSel.addEventListener('change', () => { row.status = parseInt(statusSel.value, 10); });

        tr.innerHTML = `<td class="col-num text-muted">${idx + 1}</td>
            <td><input type="number" class="form-control form-control-sm cell-price" value="${escapeHtml(row.price)}" step="0.01" min="0" placeholder="0.00" /></td>
            ${agencyAcceptsPartnership ? `<td><input type="number" class="form-control form-control-sm cell-partner" value="${escapeHtml(row.partnerPrice)}" step="0.01" min="0" placeholder="0.00" /></td>` : ''}
            <td class="col-drp"></td>
            <td class="col-status"></td>
            ${isTransport() ? `<td><input type="number" class="form-control form-control-sm cell-seats" value="${escapeHtml(row.seats)}" min="1" placeholder="1" /></td>` : ''}
            <td class="col-actions"><div class="row-actions d-flex gap-1">
                <button type="button" class="btn-delete-row" title="Remove">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="13" height="13" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" /></svg>
                </button>
            </div></td>`;

        tr.querySelector('.col-drp')?.appendChild(drpBtn);
        tr.querySelector('.col-status')?.appendChild(statusSel);

        // Sync price inputs back to state
        tr.querySelector('.cell-price')?.addEventListener('input', e => { row.price = (e.target as HTMLInputElement).value; });
        tr.querySelector('.cell-partner')?.addEventListener('input', e => { row.partnerPrice = (e.target as HTMLInputElement).value; });
        tr.querySelector('.cell-seats')?.addEventListener('input', e => { row.seats = (e.target as HTMLInputElement).value; });

        // Delete row
        tr.querySelector('.btn-delete-row')?.addEventListener('click', () => {
            if (scope === 'shared') {
                sharedRows = sharedRows.filter(r => r.id !== row.id);
                if (sharedRows.length === 0) sharedRows.push(blankRow());
                destroyPickers();
                renderSharedGrid();
            } else if (uid !== null) {
                const rows = (perUnitRows.get(uid) ?? []).filter(r => r.id !== row.id);
                if (rows.length === 0) rows.push(blankRow());
                perUnitRows.set(uid, rows);
                destroyPickers();
                renderPerUnitBlocks();
            }
        });

        tbody.appendChild(tr);
    }

    // ── Step 3: Review ────────────────────────────────
    function collectPayloadUnits(): Array<{ uid: number; name: string; rows: PriceRow[] }> {
        return [...selectedIds].map(uid => ({
            uid,
            name: units.find(u => u.id === uid)?.name ?? '',
            rows: mode === 'shared' ? sharedRows : (perUnitRows.get(uid) ?? [])
        }));
    }

    function renderReview(): void {
        reviewTbody.innerHTML = '';
        let total = 0;
        collectPayloadUnits().forEach(({ name, rows }) => {
            rows.filter(r => r.price && r.from && r.to).forEach(r => {
                const tr = document.createElement('tr');
                const missing = !r.price || !r.from || !r.to;
                tr.innerHTML = `
                    <td>${escapeHtml(name)}</td>
                    <td class="${!r.price ? 'text-danger' : ''}">${r.price || '—'}</td>
                    <td>${r.partnerPrice || r.price || '—'}</td>
                    <td class="${!r.from ? 'text-danger' : ''}">${r.from ? r.from.split('-').reverse().join('.') : '—'}</td>
                    <td class="${!r.to ? 'text-danger' : ''}">${r.to ? r.to.split('-').reverse().join('.') : '—'}</td>
                    <td>${r.status === 1 ? `<span class="badge bg-success">${locActive()}</span>` : `<span class="badge bg-secondary">${locInactive()}</span>`}</td>
                `;
                if (missing) tr.classList.add('table-danger');
                reviewTbody.appendChild(tr);
                total++;
            });
        });
        reviewSummary.textContent = `${total} price period${total !== 1 ? 's' : ''} ready to save.`;
    }

    // ── Save ──────────────────────────────────────────
    saveBtn.addEventListener('click', async () => {
        saveBtn.disabled = true;

        const payload = {
            offerId: currentOfferId,
            offerGroupName: currentOfferGroup,
            units: collectPayloadUnits().map(({ uid, rows }) => ({
                unitId: uid,
                prices: rows
                    .filter(r => r.price && r.from && r.to)
                    .map(r => ({
                        price: parseFloat(r.price),
                        partnerPriceOverride: parseFloat(r.partnerPrice || r.price),
                        from: r.from,
                        to: r.to,
                        status: r.status,
                        numberOfSeats: isTransport() ? parseInt(r.seats || '1') : null,
                        offerGroupName: currentOfferGroup,
                    }))
            })).filter(u => u.prices.length > 0)
        };

        if (payload.units.length === 0) {
            showNotification('No valid prices to save.', 'error');
            saveBtn.disabled = false;
            return;
        }

        try {
            const res = await postJson(urlSaveBulk, payload);
            if (res.success) {
                const count = payload.units.reduce((s, u) => s + u.prices.length, 0);
                showNotification(`${count} price period(s) saved.`, 'success');
                window.bootstrap.Modal.getInstance(modal)?.hide();
                location.reload();
            } else {
                showNotification('Failed to save prices.', 'error');
                saveBtn.disabled = false;
            }
        } catch {
            showNotification('An error occurred.', 'error');
            saveBtn.disabled = false;
        }
    });
}
